<script>
/* global axios */
import { useAlert } from 'dashboard/composables';

/* Credenciais e Chaves da conta: OpenAI, Supabase e chaves livres.
   Guardadas UMA vez aqui e reusadas pelas caixas e pelos fluxos — sem repetir
   a mesma chave em cada lugar. O valor cru NUNCA volta do servidor: a tela
   mostra as pontas e esconde o meio, só para reconhecer o que está gravado. */
export default {
  data() {
    return {
      loading: true,
      saving: false,
      openai: '',
      openaiDica: '',
      temOpenai: false,
      supabaseUrl: '',
      supabaseKey: '',
      supabaseKeyDica: '',
      temSupabaseKey: false,
      extras: [],        // [{nome, dica, valor}]
      remover: [],
    };
  },
  computed: {
    accountId() { return this.$route.params.accountId; },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_chaves`);
        this.openaiDica = data.openai_dica || '';
        this.temOpenai = !!data.tem_openai;
        this.supabaseUrl = data.supabase_url || '';
        this.supabaseKeyDica = data.supabase_key_dica || '';
        this.temSupabaseKey = !!data.tem_supabase_key;
        this.extras = (data.extras || []).map(e => ({ ...e, valor: '' }));
        this.remover = [];
      } catch (e) {
        useAlert('Não consegui carregar as credenciais.');
      }
      this.loading = false;
    },
    addExtra() { this.extras.push({ nome: '', dica: '', valor: '' }); },
    delExtra(i) {
      const e = this.extras[i];
      if (e.nome && e.dica) this.remover.push(e.nome);   // já existia no servidor
      this.extras.splice(i, 1);
    },
    async save() {
      this.saving = true;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/mazam_chaves`,
          {
            openai: this.openai,
            supabase_url: this.supabaseUrl,
            supabase_key: this.supabaseKey,
            extras: this.extras
              .filter(e => e.nome && e.valor)
              .map(e => ({ nome: e.nome, valor: e.valor })),
            remover: this.remover,
          });
        this.openai = '';
        this.supabaseKey = '';
        this.openaiDica = data.openai_dica || '';
        this.temOpenai = !!data.tem_openai;
        this.supabaseKeyDica = data.supabase_key_dica || '';
        this.temSupabaseKey = !!data.tem_supabase_key;
        this.extras = (data.extras || []).map(e => ({ ...e, valor: '' }));
        this.remover = [];
        useAlert('Credenciais salvas.');
      } catch (e) {
        useAlert(e.response?.data?.error || 'Não foi possível salvar.');
      }
      this.saving = false;
    },
  },
};
</script>

<template>
  <div class="p-6 mx-auto max-w-3xl">
    <h2 class="mb-1 text-lg font-semibold text-n-slate-12">Credenciais e Chaves</h2>
    <p class="mb-5 text-sm text-n-slate-11">
      Guarde aqui as chaves que você usa em mais de um lugar. As caixas de
      atendimento podem usar a chave desta tela ou ter a sua própria, e os
      blocos do fluxo (HTTP, Script) podem chamar as chaves livres pelo nome.
    </p>

    <p v-if="loading" class="text-sm text-n-slate-11">Carregando…</p>

    <template v-else>
      <!-- OpenAI -->
      <section class="ck-card">
        <h3>🤖 OpenAI</h3>
        <p class="ck-hint">
          Usada pelas IAs de atendimento. Quem não tiver chave própria na caixa
          usa esta.
        </p>
        <div v-if="temOpenai" class="ck-atual">
          Gravada: <b>{{ openaiDica }}</b>
        </div>
        <input v-model="openai" type="text" class="ck-in"
               :placeholder="temOpenai ? 'digite uma nova para substituir' : 'sk-…'" />
      </section>

      <!-- Supabase -->
      <section class="ck-card">
        <h3>🗄️ Supabase</h3>
        <p class="ck-hint">
          Endereço do projeto e a chave de serviço, usadas pelo bloco Supabase
          do fluxo.
        </p>
        <label class="ck-lbl">URL do projeto</label>
        <input v-model="supabaseUrl" type="text" class="ck-in mb-2"
               placeholder="https://xxxx.supabase.co" />
        <label class="ck-lbl">Chave (service key)</label>
        <div v-if="temSupabaseKey" class="ck-atual">
          Gravada: <b>{{ supabaseKeyDica }}</b>
        </div>
        <input v-model="supabaseKey" type="text" class="ck-in"
               :placeholder="temSupabaseKey ? 'digite uma nova para substituir' : 'eyJ…'" />
      </section>

      <!-- Chaves livres -->
      <section class="ck-card">
        <h3>🔑 Outras chaves</h3>
        <p class="ck-hint">
          Crie quantas quiser. No fluxo você usa pelo nome, escrevendo
          <code>&#123;&#123;secret:NOME&#125;&#125;</code> no bloco HTTP ou no Script.
        </p>
        <div v-for="(e,i) in extras" :key="'k'+i" class="ck-row">
          <input v-model="e.nome" type="text" class="ck-in ck-nome"
                 placeholder="NOME_DA_CHAVE" :disabled="!!e.dica" />
          <input v-model="e.valor" type="text" class="ck-in"
                 :placeholder="e.dica ? e.dica + ' — digite para trocar' : 'valor'" />
          <button type="button" class="ck-x" title="Excluir" @click="delExtra(i)">✕</button>
        </div>
        <button type="button" class="ck-add" @click="addExtra">＋ nova chave</button>
      </section>

      <p class="mb-4 text-xs text-n-slate-11">
        Por segurança, a chave inteira nunca volta para a tela — você vê o começo
        e o fim, com o meio escondido. Campo em branco mantém o que já está
        gravado.
      </p>

      <button type="button" class="ck-save" :disabled="saving" @click="save">
        {{ saving ? 'Salvando…' : 'Salvar credenciais' }}
      </button>
    </template>
  </div>
</template>

<style scoped>
.ck-card {
  background: rgba(148, 163, 184, 0.08);
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
}
.ck-card h3 { font-size: 14px; font-weight: 700; margin-bottom: 2px; }
.ck-hint { font-size: 12px; opacity: 0.7; margin-bottom: 10px; line-height: 1.45; }
.ck-hint code { background: rgba(148, 163, 184, 0.18); padding: 0 4px; border-radius: 4px; }
.ck-lbl { display: block; font-size: 12px; font-weight: 600; margin-bottom: 3px; }
.ck-atual {
  font-size: 12px; margin-bottom: 6px; padding: 6px 10px; border-radius: 8px;
  background: rgba(0, 168, 132, 0.12); color: #01916f;
}
.dark .ck-atual { color: #4fd1b0; }
.ck-atual b { font-family: ui-monospace, monospace; letter-spacing: .3px; }
.ck-in {
  width: 100%; padding: 8px 10px; border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35); background: transparent;
}
.ck-row { display: flex; gap: 8px; margin-bottom: 6px; align-items: center; }
.ck-nome { max-width: 200px; text-transform: uppercase; }
.ck-x {
  width: 30px; flex: 0 0 30px; border-radius: 8px; font-size: 13px;
  background: rgba(241, 92, 109, 0.12); color: #f15c6d;
}
.ck-add {
  font-size: 12px; color: #00a884; background: rgba(0, 168, 132, 0.1);
  border: 1px dashed rgba(0, 168, 132, 0.35); border-radius: 8px; padding: 5px 12px;
}
.ck-save {
  background: #00a884; color: #fff; font-size: 14px; font-weight: 600;
  padding: 10px 22px; border-radius: 8px;
}
.ck-save:hover:not(:disabled) { background: #01916f; }
.ck-save:disabled { opacity: .55; cursor: default; }
</style>
